import { CommonModule } from '@angular/common';
import { Component, EventEmitter, forwardRef, inject, OnInit, Output, Provider } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import {
  AlertComponent,
  ButtonComponent,
  CardComponent,
  CheckboxComponent,
  FormGroupComponent,
  InputComponent,
  MbsSize,
  RadioComponent,
  RadioDescription
} from 'mbs-ui-kit';
import { HelpLinkDirective } from 'mbs-ui-shared@help/helpLink.directive';
import { PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { EnvironmentService } from 'mbs-ui-shared@services/environments.service';
import { PlanTypePipe } from 'mbs-ui-shared@utils/pipes/plan-type.pipe';
import { ObservedValueOf } from 'rxjs';
import { ImagePath, PlanNameStepValue } from 'wizards/models/plan-name-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { StepBase } from 'wizards/steps/StepBase.class';

const ibbHelpLink = '/AP/Help/backup/ibb/nbf';

const PlanNameStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => PlanNameStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-plan-name-step',
  templateUrl: './plan-name-step.component.html',
  providers: [PlanNameStepValueAccessor],
  imports: [
    AlertComponent,
    CheckboxComponent,
    CommonModule,
    FormGroupComponent,
    I18NextPipe,
    InputComponent,
    RadioComponent,
    RadioDescription,
    ReactiveFormsModule,
    ButtonComponent,
    CardComponent,
    PlanTypePipe,
    HelpLinkDirective
  ]
})
export class PlanNameStepComponent extends StepBase<PlanNameStepValue> implements OnInit {
  ability = inject(AbilityService);
  mainService: RemoteManagementWizardsService;
  i18nPipe = inject(I18NextPipe);

  @Output() restoreOnce = new EventEmitter();
  @Output() copyPlan = new EventEmitter();

  private stepService = inject(WizardStepsService);
  private environment = inject(EnvironmentService);
  private ABTest: ObservedValueOf<ReturnType<typeof this.stepService.pipeABTest>> = {};

  public readonly MbsSize = MbsSize;
  public readonly elementsSelector = {
    css: {
      planNameRestoreRadio: 'mbs-wizard_restore-radio',
      planNameBackupCheckbox: 'mbs-wizard_backup-checkbox'
    }
  };
  public readonly runRestoreOnceSubtitle = this.i18nPipe.transform('wizards:run_restore_once_subtitle', { returnObjects: true });
  protected readonly isProduction = !this.ability.can('read', 'AgentLocal') && !window.location.host.startsWith('localhost');

  public bestForAdvices: Partial<Record<PlanTypes, string>> = {
    [PlanTypes.Plan]: this.i18nPipe.transform('shared:sidepanel-backup.addNewPlan.descriptions.filesBackup.bestFor'),
    [PlanTypes.BackupDiskImagePlan]: this.i18nPipe.transform('shared:sidepanel-backup.addNewPlan.descriptions.imageBasedBackup.bestFor')
  };
  public descriptions: Partial<Record<PlanTypes, string>> = {
    [PlanTypes.Plan]: this.i18nPipe.transform('shared:sidepanel-backup.addNewPlan.descriptions.filesBackup.list', { returnObjects: true }),
    [PlanTypes.BackupDiskImagePlan]: this.i18nPipe.transform('shared:sidepanel-backup.addNewPlan.descriptions.imageBasedBackup.list', {
      returnObjects: true
    })
  };
  public helpLinks: {
    windows: Partial<Record<PlanTypes, string>>;
    linux: Partial<Record<PlanTypes, string>>;
    mac: Partial<Record<PlanTypes, string>>;
  } = {
    windows: {
      [PlanTypes.Plan]: '/AP/Help/backup/file/nbf',
      [PlanTypes.BackupDiskImagePlan]: ibbHelpLink
    },
    linux: {
      [PlanTypes.Plan]: '/AP/Help/backup/file/linux-nbf',
      [PlanTypes.BackupDiskImagePlan]: ibbHelpLink
    },
    mac: {
      [PlanTypes.Plan]: '/AP/Help/backup/file/mac-nbf',
      [PlanTypes.BackupDiskImagePlan]: ibbHelpLink
    }
  };

  public currentHelpLink: string = this.helpLinks.windows[this.planType];

  get disableSaveRestorePlan(): boolean {
    return !!this.mainService?.sessionId || !!this.mainService?.targetComputerData;
  }

  get hasAdvice(): boolean {
    return this.isNBF && (this.planType === PlanTypes.Plan || this.planType === PlanTypes.BackupDiskImagePlan);
  }

  get imageSrc(): string {
    return ImagePath[this.planType] || ImagePath[PlanTypes.BackupDiskImagePlan];
  }

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);

    super();

    this.mainService = mainService;

    if (this.isLinux) this.currentHelpLink = this.helpLinks.linux[this.planType];
    if (this.isMac) this.currentHelpLink = this.helpLinks.mac[this.planType];
  }

  ngOnInit(): void {
    this.initForm();
    this.initEvents();
  }

  initForm(): void {
    this.stepForm = new UntypedFormGroup({
      Name: new FormControl('', [Validators.required, Validators.minLength(2)]),
      SavePlanInCloud: new FormControl(false)
    });

    this.initFormEvents();
  }

  initEvents(): void {
    this.stepService
      .pipeProperty('ABTest')
      .pipe(untilDestroyed(this))
      .subscribe((data) => (this.ABTest = data));
  }

  onStepFormChange(value: PlanNameStepValue): void {
    if (this.isRestore && !this.stepForm.touched) this.changeSavePlanHandler(value.SavePlanInCloud, false);
    if (this.stepForm.dirty || this.stepForm.touched) this.value = { ...value, valid: this.stepForm.valid };
  }

  changeSavePlanHandler(event: boolean, fromLayout = true): void {
    if (!fromLayout) this.stepForm.markAsTouched();
    if (event) return this.stepForm.get('Name').enable({ emitEvent: false });

    if (this.isRestore) this.restoreOnce.emit(true);

    this.stepForm.get('Name').disable({ emitEvent: false });
  }

  toggleABTest(): void {
    if (this.isNBF && (this.planType === PlanTypes.Plan || this.planType === PlanTypes.BackupDiskImagePlan)) {
      this.stepService.updateProperty('ABTest', 'DestinationPlusRetentionStep', !this.ABTest.DestinationPlusRetentionStep);
    }
  }
}
